// Considered: rendering consequences inline, unfoldable only via the parent row — rejected. The
// "also recorded on … · N entries" toggle plus per-child inspect is its own composable unit
// (design/metaspace-tools/metaspace-tools-v2.jsx:183-200, `unfolded` state + child cards) and
// `StreamEntryMolecule` would otherwise carry two independent expand/collapse concerns (the day
// group's collapse and this fold). See spec Reuse Audit (specs/stream-trajectory-tools.md).

import React from 'react';
import { useTranslation } from 'localization';
import type { StreamEntry } from 'types/stream';
import type { InspectorPayload } from 'types/inspector';
import PeekAtom from '../../atoms/Peek/PeekAtom';
import GlyphAtom from '../../atoms/Glyph/GlyphAtom';
import InspectableMolecule from '../Inspectable/InspectableMolecule';
import './ConsequenceFoldMolecule.scss';

export interface ConsequenceFoldMoleculeProps {
  consequences: StreamEntry[];
  /** Fold state is PER ENTRY (owned by the parent chord's correlationId), not per day-group. */
  folded: boolean;
  onToggle: () => void;
  /** Builds the inspector payload for a single consequence row on click. */
  buildPayload: (entry: StreamEntry) => InspectorPayload;
  onInspect: (payload: InspectorPayload) => void;
  className?: string;
}

const ConsequenceFoldMolecule: React.FC<ConsequenceFoldMoleculeProps> = ({
  consequences,
  folded,
  onToggle,
  buildPayload,
  onInspect,
  className,
}) => {
  const { t } = useTranslation();

  if (consequences.length === 0) return null;

  return (
    <div className={['consequence-fold-molecule', className].filter(Boolean).join(' ')}>
      <PeekAtom peek={t('toolInspector.consequenceFold.peek', { count: consequences.length, defaultValue: `${consequences.length} folded children` })}>
        <button type="button" className="consequence-fold-molecule__toggle focus-ring" onClick={onToggle}>
          <GlyphAtom glyph={folded ? 'lucide:corner-down-right' : 'lucide:corner-left-up'} />
          {t('stream.panel.alsoRecordedOn')}{' '}
          <span className="consequence-fold-molecule__count">
            {folded ? t('stream.panel.entriesCount', { count: consequences.length }) : t('stream.panel.fold')}
          </span>
        </button>
      </PeekAtom>
      {!folded && (
        <div className="consequence-fold-molecule__children">
          {consequences.map((entry) => (
            <InspectableMolecule key={entry.seq} payload={buildPayload(entry)} onInspect={onInspect} block>
              <div className="consequence-fold-molecule__child">
                <div className="consequence-fold-molecule__child-kind">{entry.entryType.toUpperCase()}</div>
                <div className="consequence-fold-molecule__child-value">
                  {Object.values(entry.content ?? {}).map(String).join(', ')}
                </div>
              </div>
            </InspectableMolecule>
          ))}
        </div>
      )}
    </div>
  );
};

ConsequenceFoldMolecule.displayName = 'ConsequenceFoldMolecule';

export default ConsequenceFoldMolecule;
